import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { trackPageView, setAnalyticsUserId } from '@/lib/google-analytics-init';
import { emitInternalPageView } from '@/lib/analytics/internalPageView';
import { hasProAccessRole } from '@/lib/proAccessRole';
import { getTitleForPath } from '@/lib/route-titles';

/**
 * KB-2607: the single source of GA4 `page_view` events for pro.
 * KB-2619: also the single source of `document.title` for every route
 * rendered inside `AuthLayout` — see `route-titles.ts` for the map.
 * (`/logout` is the one App.tsx route outside `AuthLayout`, so it sets its
 * own title directly.)
 *
 * Rendered inside `AuthLayout` (src/App.tsx), which itself doesn't mount
 * its children until `AuthContext`'s `loading` flag resolves — so this
 * component never even exists in the tree before auth has settled.
 * Still gates the GA4 call on `uid` directly rather than trusting placement
 * alone: pro has no anonymous users, and an unauthenticated visitor on a
 * public route (e.g. `/login`) resolves `loading` while leaving
 * `currentUser` null — that visit must stay silent too, which is a
 * per-render check, not just a mount-time one. The title update is NOT
 * gated on `uid` — every route needs a distinct tab title regardless of
 * auth state.
 */
export function AnalyticsRouteTracker() {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const uid = currentUser?.uid ?? null;
  // KB-4771 round 2: the internal page view counts only a session whose profile
  // passes the allowlist. A refused sign-in holds a live session for a moment
  // (PublicRoute redirects it to /dashboard before the gate signs it out), and
  // that view must not read as a director using pro. Until the profile resolves
  // this is false, so the view waits for it and then fires once.
  const admitted = uid !== null && hasProAccessRole(userProfile);

  useEffect(() => {
    if (!uid) return;
    setAnalyticsUserId(uid);
  }, [uid]);

  useEffect(() => {
    // Title must be set before trackPageView fires (KB-2619 AC4) — GA4
    // would otherwise auto-collect the PREVIOUS route's document.title.
    const title = getTitleForPath(location.pathname);
    document.title = title;

    if (!uid) return;
    trackPageView(location.pathname, title);
  }, [location.pathname, uid]);

  // KB-4771: the same view, into the internal sink, as a route pattern. Its own
  // effect so the allowlist wait cannot delay or repeat the GA4 call above.
  useEffect(() => {
    if (!admitted) return;
    emitInternalPageView(location.pathname);
  }, [location.pathname, admitted]);

  return null;
}
